import { GetStaticPropsContext } from "next";
import { Menu } from "@lib/menu";
import { fetchMenu } from "@lib/frontend/menus";
import { StrapiMenus } from "@lib/strapi/menus";
import { SiteSettingsService } from "@lib/strapi/services/content-types/siteSettings.service";

/**
 * Footer data, from the CMS rather than a hardcoded struct.
 *
 * The v4 `footer` single type is gone: it was never a page, it was site
 * settings (D2). Contact details now come from `site-setting`, which the
 * header, the contact page and the LocalBusiness JSON-LD also read — one
 * source, so the phone number cannot disagree with itself across the site.
 *
 * The newsletter is deliberately absent: it moved to the contact page (D15) so
 * the footer is not stacking competing calls to action.
 */
export interface FooterProps {
  menu: Menu | null;
  /** the bottom legal row (CMS menu `footer-legal`, owner 2026-09-07) */
  legalMenu?: Menu | null;
  telephone?: string;
  mobile?: string;
  email?: string;
  addressStreet?: string;
  addressLocality?: string;
  addressPostalCode?: string;
  workingHours?: string;
  /** machine-readable hours (site-settings, P8p) — feed the business schema
   *  and the sidebar label; workingHours stays free display text */
  hoursOpens?: string;
  hoursCloses?: string;
  /** comma-separated schema.org day names («Monday,…») */
  hoursDays?: string;
  googleMapsUrl?: string;
  googleBusinessProfileUrl?: string;
  googleWriteReviewUrl?: string;
  legalName?: string;
  eopyyContracted?: boolean;
}

export const getFooterProps = async (
  context: GetStaticPropsContext
): Promise<FooterProps> => {
  const locale = context.locale ?? "el";
  const [menu, legalMenu, settings] = await Promise.all([
    fetchMenu(context, StrapiMenus.footerMenu),
    fetchMenu(context, StrapiMenus.footerLegal),
    SiteSettingsService.fetchSingle(null, locale),
  ]);

  return {
    menu: menu ?? null,
    legalMenu: legalMenu ?? null,
    telephone: (settings as any)?.telephone,
    mobile: (settings as any)?.mobile,
    email: (settings as any)?.email,
    addressStreet: (settings as any)?.addressStreet,
    addressLocality: (settings as any)?.addressLocality,
    addressPostalCode: (settings as any)?.addressPostalCode,
    workingHours: (settings as any)?.workingHours,
    hoursOpens: (settings as any)?.hoursOpens,
    hoursCloses: (settings as any)?.hoursCloses,
    hoursDays: (settings as any)?.hoursDays,
    googleMapsUrl: (settings as any)?.googleMapsUrl,
    googleBusinessProfileUrl: (settings as any)?.googleBusinessProfileUrl,
    googleWriteReviewUrl: (settings as any)?.googleWriteReviewUrl,
    legalName: (settings as any)?.legalName,
    eopyyContracted: (settings as any)?.eopyyContracted,
  };
};
